import { SkillTraining, WeaponClass } from '../types/character';
import type { Character, Characteristics, Talent, Weapon } from '../types/character';
import type { CombatActor } from '../types/combat-actor';
import {
  RollResult,
  Modifier,
  ModifierSource,
  Difficulty,
  RollType,
  DamageRoll,
  CombatRoll,
  AttackType,
  D100Roll,
  DegreesOfSuccess,
  DamageType
} from '../types/dice';
import type { CharacterProficiencies } from '../types/proficiency';
import { ProficiencyService } from './ProficiencyService';
import { TraitService } from './TraitService';
import { useRollHistory } from '../stores/rollHistory';
import { ModifiedWeapon } from './EquipmentModifierService';
import { RuleEvaluationResult } from './SpecialRulesService';
import { gameDataService } from './GameDataService';
import { useCharacterStore } from '../stores/character';
import { getStateDefinition } from '../data/combatStateDefinitions';
import { TalentsService } from './TalentsService';
import { CareerAbilityService } from './CareerAbilityService';

export interface LogContext {
  title?: string;
  parentEntryId?: string;
  tags?: string[];
  forceLog?: boolean; // If true, logs even if it might not normally
  compactDamageDetails?: boolean;
}

export interface FuryRoll {
  confirmRoll: number;
  confirmSuccess: boolean;
  bonusDamage: number;
}

export interface TearingInfo {
  allRolls: number[];
  discardedRoll: number;
  keptRolls: number[];
  finalValue: number;
}

export class DiceService {
  private rng: () => number;
  private rollHistory: RollResult[] = [];
  private maxHistoryEntries: number = 100;
  private sessionStart: Date = new Date();

  constructor(seedableRng?: () => number) {
    this.rng = seedableRng || (() => Math.random());
  }

  /**
   * Get the RNG function used by this service (for sharing with other services)
   */
  getRng(): () => number {
    return this.rng;
  }

  /**
   * Get roll history
   */
  /**
   * Add roll to history (newest first)
   */
  private addToHistory(roll: RollResult): void {
    this.rollHistory.unshift(roll);
    if (this.rollHistory.length > this.maxHistoryEntries) {
      this.rollHistory.pop();
    }
  }

  /**
   * Log a roll to the central history store
   */
  private logRoll(roll: RollResult | DamageRoll, context?: LogContext): string | undefined {
    if (context?.forceLog === false) return undefined;

    const { addEntry, updateEntry } = useRollHistory.getState();

    // If we have a parentEntryId, we are strictly updating an existing entry (like damage)
    if (context?.parentEntryId) {
      if ('baseDamage' in roll) {
        // It's a DamageRoll
        const damageDetails = `${roll.dieRoll}${roll.modifiers.length > 0 ? ' + ' + roll.modifiers.reduce((s, m) => s + m.value, 0) : ''}`;
        updateEntry(context.parentEntryId, {
          relatedRolls: [
            {
              title: context.title || 'Damage',
              total: roll.total,
              details: `${damageDetails} (Pen: ${roll.penetration})`
            }
          ]
        });
      }
      return context.parentEntryId;
    }

    // Otherwise create a new entry
    if ('target' in roll) {
      // It's a RollResult/CombatRoll (d100)
      const entryId = addEntry({
        type: roll.tags.includes('combat')
          ? 'combat'
          : roll.tags.includes(RollType.Skill)
            ? 'skill'
            : 'other',
        title: context?.title || 'Die Roll',
        total: roll.raw,
        result: roll.success ? 'success' : 'failure',
        details: `Rolled ${roll.raw} vs ${roll.target} (${roll.degrees > 0 ? '+' : ''}${roll.degrees} DoS)`,
        tags: [...(roll.tags || []), ...(context?.tags || [])]
      });
      return entryId;
    }

    return undefined;
  }

  /**
   * Roll a D100 (1-100)
   */
  rollD100(): D100Roll {
    return Math.floor(this.rng() * 100) + 1;
  }

  /**
   * Roll multiple dice of specified sides
   */
  private rollMultiple(sides: number, count: number): number[] {
    const results: number[] = [];
    for (let i = 0; i < count; i++) {
      results.push(Math.floor(this.rng() * sides) + 1);
    }
    return results;
  }

  /**
   * Roll a single die of specified sides
   */
  rollDie(sides: number): number {
    return Math.floor(this.rng() * sides) + 1;
  }

  /**
   * Calculate degrees of success/failure
   */
  calculateDegrees(roll: number, target: number): DegreesOfSuccess {
    const difference = target - roll;
    const degrees = Math.floor(Math.abs(difference) / 10);

    if (degrees === 0) {
      return 0;
    }

    return degrees * (difference >= 0 ? 1 : -1);
  }

  /**
   * Apply modifiers to a base target number
   */
  private applyModifiers(baseTarget: number, modifiers: Modifier[]): number {
    return modifiers.reduce((target, modifier) => target + modifier.value, baseTarget);
  }

  /**
   * Get skill training bonus
   */
  getSkillTrainingBonus(training: SkillTraining): number {
    switch (training) {
      case SkillTraining.Untrained:
        return -20;
      case SkillTraining.Trained:
        return 0;
      case SkillTraining.Experienced:
        return 10;
      case SkillTraining.Adept:
        return 20;
      default:
        return 0;
    }
  }

  /**
   * Create a modifier from skill training
   */
  private createSkillModifier(skillName: string, training: SkillTraining): Modifier {
    return {
      name: `${skillName} Training`,
      value: this.getSkillTrainingBonus(training),
      source: ModifierSource.Skill,
      description: `${training} level training in ${skillName}`
    };
  }

  /**
   * Create a modifier from characteristic
   */
  private createCharacteristicModifier(characteristicName: string, value: number): Modifier {
    const displayNames: Record<string, string> = {
      weaponSkill: 'WS',
      ballisticSkill: 'BS',
      strength: 'Strength',
      toughness: 'Toughness',
      agility: 'Agility',
      intelligence: 'Intelligence',
      perception: 'Perception',
      willpower: 'Willpower',
      fellowship: 'Fellowship',
      influence: 'Influence'
    };

    const displayName = displayNames[characteristicName] || characteristicName;

    return {
      name: displayName,
      value: value,
      source: ModifierSource.Characteristic,
      description: `${displayName} characteristic bonus`
    };
  }

  /**
   * Get fatigue modifier if character has fatigue > 0.
   * Returns empty if Frenzied or Duty Unto Death is active (they suppress fatigue effects).
   */
  private getFatigueModifier(
    character: CombatActor & { progression?: Character['progression'] }
  ): Modifier[] {
    // Check if fatigue is suppressed by active combat states
    const { isStateActive } = useCharacterStore.getState();
    if (isStateActive('frenzied') || isStateActive('duty-unto-death')) {
      return [];
    }

    const current = character.progression?.fatigueTracking?.current || 0;
    if (current <= 0) return [];
    return [
      {
        name: 'Fatigue',
        value: -10,
        source: ModifierSource.Situational,
        description: `Fatigued (${current}/${character.progression?.fatigueTracking?.maximum || 0})`
      }
    ];
  }

  /**
   * Map from characteristic keys (keyof Characteristics) to combat state modifier target names.
   */
  private static readonly CHARACTERISTIC_TARGET_MAP: Record<string, string> = {
    weaponSkill: 'Weapon Skill',
    ballisticSkill: 'Ballistic Skill',
    strength: 'Strength',
    toughness: 'Toughness',
    agility: 'Agility',
    intelligence: 'Intelligence',
    perception: 'Perception',
    willpower: 'Willpower',
    fellowship: 'Fellowship',
    influence: 'Influence'
  };

  /**
   * Get combat state modifiers filtered by the target being tested.
   * Reads active states from the combat states store, checks suppression,
   * and returns only modifiers whose target matches `targetName` or is "all".
   *
   * @param targetName - The display name of the skill/characteristic being tested
   *                     (e.g., "Ballistic Skill", "Weapon Skill", "Dodge")
   */
  getCombatStateModifiers(targetName: string): Modifier[] {
    const { combatStates } = useCharacterStore.getState();
    const activeIds = Object.keys(combatStates);
    if (activeIds.length === 0) return [];

    // Build set of suppressed state IDs
    const suppressedIds = new Set<string>();
    for (const id of activeIds) {
      const def = getStateDefinition(id);
      if (def?.suppresses) {
        for (const suppressed of def.suppresses) {
          suppressedIds.add(suppressed);
        }
      }
    }

    const modifiers: Modifier[] = [];

    for (const id of activeIds) {
      const definition = getStateDefinition(id);
      if (!definition || definition.modifiers.length === 0) continue;

      // Skip if suppressed by another active state's `suppresses` array
      if (suppressedIds.has(id)) continue;

      // Skip if this state's `suppressedBy` includes an active state
      const isSuppressedBy = definition.suppressedBy.some((suppressorId) =>
        activeIds.includes(suppressorId)
      );
      if (isSuppressedBy) continue;

      // Filter modifiers by target match
      for (const mod of definition.modifiers) {
        if (mod.target === targetName || mod.target === 'all') {
          modifiers.push({
            name: mod.description,
            value: mod.value,
            source: ModifierSource.Situational,
            description: mod.description
          });
        }
      }
    }

    return modifiers;
  }

  /**
   * Resolve a characteristic key to its combat state target name.
   */
  private resolveCharacteristicTarget(characteristicKey: string): string {
    return DiceService.CHARACTERISTIC_TARGET_MAP[characteristicKey] || characteristicKey;
  }

  /**
   * Perform a basic roll with modifiers
   */
  rollWithModifiers(
    baseTarget: number,
    modifiers: Modifier[] = [],
    rollType: RollType = RollType.Other,
    logContext?: LogContext
  ): RollResult {
    const raw = this.rollD100();
    const target = this.applyModifiers(baseTarget, modifiers);
    const degrees = this.calculateDegrees(raw, target);
    const success = raw <= target;

    const result: RollResult = {
      raw,
      target,
      modifiers: [...modifiers],
      total: target,
      degrees,
      success,
      tags: [rollType],
      timestamp: new Date()
    };

    // Add to local history
    this.addToHistory(result);

    const entryId = this.logRoll(result, logContext);
    if (entryId) result.entryId = entryId;
    return result;
  }

  /**
   * Roll against a characteristic
   */
  rollCharacteristic(
    character: CombatActor,
    characteristicName: keyof Characteristics,
    additionalModifiers: Modifier[] = [],
    difficulty: Difficulty = Difficulty.Challenging,
    logContext?: LogContext
  ): RollResult {
    const characteristicValue = character.characteristics[characteristicName];

    const modifiers: Modifier[] = [
      this.createCharacteristicModifier(characteristicName, characteristicValue),
      {
        name: 'Difficulty',
        value: difficulty,
        source: ModifierSource.Situational,
        description: `${Difficulty[difficulty]} difficulty`
      },
      ...this.getFatigueModifier(character),
      ...this.getCombatStateModifiers(this.resolveCharacteristicTarget(characteristicName)),
      ...additionalModifiers
    ];

    return this.rollWithModifiers(0, modifiers, RollType.Characteristic, logContext);
  }

  /**
   * Roll a skill test
   */
  rollSkill(
    character: Character,
    skillId: string,
    additionalModifiers: Modifier[] = [],
    difficulty: Difficulty = Difficulty.Challenging,
    logContext?: LogContext
  ): RollResult {
    const skill = character.skills.find((s) => s.id === skillId);

    if (!skill) {
      throw new Error(`Skill with ID "${skillId}" not found on character`);
    }

    const characteristicValue = character.characteristics[skill.characteristic];

    const characteristicTarget = this.resolveCharacteristicTarget(skill.characteristic);
    const skillModifiers = this.getCombatStateModifiers(skill.name);
    // Only add characteristic-level modifiers if the skill name differs from the characteristic target
    // to avoid double-counting (e.g., "Ballistic Skill" skill + "ballisticSkill" characteristic)
    const charModifiers =
      skill.name === characteristicTarget ? [] : this.getCombatStateModifiers(characteristicTarget);

    // Check for Talented talent bonus (+10 to skill tests)
    const talentedBonus = TalentsService.getTalentedBonusForSkill(character.talents, skillId);

    // For untrained Basic skills, use half the characteristic value (Ch03 rule)
    // For untrained Advanced skills, the -20 penalty from getSkillTrainingBonus applies
    const isUntrainedBasic = skill.training === SkillTraining.Untrained && skill.type === 'Basic';
    const effectiveCharValue = isUntrainedBasic
      ? Math.floor(characteristicValue / 2)
      : characteristicValue;

    const modifiers: Modifier[] = [
      this.createCharacteristicModifier(skill.characteristic, effectiveCharValue),
      // Skip the -20 untrained penalty for Basic skills since we already halved the characteristic
      ...(isUntrainedBasic ? [] : [this.createSkillModifier(skill.name, skill.training)]),
      {
        name: 'Difficulty',
        value: difficulty,
        source: ModifierSource.Situational,
        description: `${Difficulty[difficulty]} difficulty`
      },
      ...this.getFatigueModifier(character),
      ...skillModifiers,
      ...charModifiers,
      ...additionalModifiers
    ];

    if (talentedBonus > 0) {
      modifiers.push({
        name: 'Talented',
        value: talentedBonus,
        source: ModifierSource.Talent,
        description: `Talented (${skill.name})`
      });
    }

    // Career ability skill modifiers (Seeker of Lore: +1 DoS on Commerce/Inquiry/Evaluate)
    const careerSkillMods = CareerAbilityService.getSkillModifiers(character, skill.name);
    modifiers.push(...careerSkillMods);

    const result = this.rollWithModifiers(0, modifiers, RollType.Skill, logContext);
    result.tags.push(skill.name, skill.characteristic);

    return result;
  }

  /**
   * Roll weapon damage (Basic implementation without RF/Reroll logic, kept for compatibility if needed)
   * Deprecated: Use rollWeaponDamage for full combat logic
   */
  rollDamage(
    baseDamageString: string,
    strengthBonus: number = 0,
    degreesOfSuccess: number = 0,
    modifiers: Modifier[] = [],
    penetration: number = 0,
    damageType: DamageType = DamageType.Impact
  ): DamageRoll {
    // ... (Existing implementation if needed, or redirect)
    // For now, simple implementation or throw error?
    // Let's keep the simple logic for now as fallback
    const damageMatch = baseDamageString.match(/(\d+)d(\d+)(?:\+(\d+))?/);
    if (!damageMatch) throw new Error(`Invalid damage string: ${baseDamageString}`);
    const [, diceCount, diceSides, fixedBonus] = damageMatch;
    const rolls = this.rollMultiple(parseInt(diceSides), parseInt(diceCount));
    const rollTotal = rolls.reduce((sum, roll) => sum + roll, 0);
    const bonus = parseInt(fixedBonus || '0');
    const modifierTotal = modifiers.reduce((sum, mod) => sum + mod.value, 0);
    const total = rollTotal + bonus + strengthBonus + degreesOfSuccess + modifierTotal;

    return {
      weapon: 'Unknown',
      baseDamage: baseDamageString,
      strengthBonus,
      degreesOfSuccess,
      modifiers: [...modifiers],
      total: Math.max(0, total),
      penetration,
      type: damageType,
      dieRoll: rollTotal // Added dieRoll to DamageRoll interface conceptually
    } as DamageRoll;
  }

  /**
   * Roll Weapon Damage (Full Combat Logic: RF, Rerolls, Tearing, Logging)
   */
  rollWeaponDamage(
    modifiedWeapon: ModifiedWeapon,
    character: CombatActor,
    allModifiers: Modifier[],
    difficulty: Difficulty,
    damageModifiers: Modifier[] = [],
    ruleEffects: RuleEvaluationResult['effects'] = [],
    allowRerolls: boolean = true,
    logContext?: LogContext
  ): { damageRoll?: DamageRoll; furyRolls: FuryRoll[]; tearingInfo?: TearingInfo } {
    // Melee damage adds Strength Bonus. RT Core's own worked example for
    // Unnatural Characteristic is Strength ("a creature with a Strength of 41
    // normally has a 4 Strength Bonus. With this Trait, its Strength Bonus
    // increases to 8"), so the multiplier applies to the bonus added here.
    const strengthBonus =
      modifiedWeapon.class === WeaponClass.Melee
        ? Math.floor(character.characteristics.strength / 10) *
          TraitService.getUnnaturalMultiplier(character.traits, 'strength')
        : 0;

    // Parse damage
    const damageMatch = modifiedWeapon.finalDamage.match(/(\d+)d(\d+)(?:\+(\d+))?/);
    if (!damageMatch) {
      // The damage is not a dice formula. Launchers print "Variable" (the loaded round
      // decides) and several grenades print "-" or "Special" because they deal none.
      // The fallbacks below would have rolled 1d10 in every such case, inventing a
      // figure the source does not give — so roll nothing and let the caller, which
      // already treats damageRoll as optional, report the hit without damage.
      return { furyRolls: [] };
    }
    const baseDiceCount = parseInt(damageMatch[1]);
    const dieType = parseInt(damageMatch[2]);
    const fixedBonus = damageMatch[3] ? parseInt(damageMatch[3]) : 0;

    // Calculate extra dice from diceAdditional effects (e.g., Tearing, Maximal)
    let extraDice = 0;
    let discardLowest = false;
    let tearingInfo: TearingInfo | undefined;

    ruleEffects.forEach((effect) => {
      if (effect.type === 'diceAdditional' && effect.target === 'damageRoll') {
        const additionalDice = Number(effect.value) || 0;
        extraDice += additionalDice;
        // Check for discardLowest flag (Tearing rule)
        if ((effect as any).discardLowest === true) {
          discardLowest = true;
        }
      }
    });

    // Roll base weapon dice + any extra dice from rules (e.g., Tearing, Maximal)
    const totalDiceToRoll = baseDiceCount + extraDice;
    const allDiceRolls: number[] = [];

    // Check for Proven(X) minimum die value
    let diceMinimum = 0;
    ruleEffects.forEach((effect) => {
      if (effect.type === 'diceMinimum' && effect.target === 'damageRoll') {
        const min = Number(effect.value) || 0;
        if (min > diceMinimum) diceMinimum = min;
      }
    });

    for (let i = 0; i < totalDiceToRoll; i++) {
      let roll = Math.floor(this.rng() * dieType) + 1;
      if (diceMinimum > 0 && roll < diceMinimum) roll = diceMinimum;
      allDiceRolls.push(roll);
    }

    let finalRoll: number;
    let contributingDice: number[] = [];

    // Handle Tearing: discard lowest die
    if (discardLowest && allDiceRolls.length > 1) {
      const sortedRolls = [...allDiceRolls].sort((a, b) => a - b);
      const discardedDie = sortedRolls[0];
      const keptDice = sortedRolls.slice(1);
      finalRoll = keptDice.reduce((sum, d) => sum + d, 0);
      contributingDice = keptDice;

      // For standard 1d10 weapons with Tearing, we keep 1 die
      // For multi-dice weapons, we'd keep all but the lowest
      if (keptDice.length === 1) {
        finalRoll = keptDice[0];
      }

      tearingInfo = {
        allRolls: allDiceRolls,
        discardedRoll: discardedDie,
        keptRolls: keptDice,
        finalValue: finalRoll
      };

      console.log(
        `[Tearing] Rolled ${allDiceRolls.join(', ')} -> Discarded ${discardedDie}, kept ${keptDice.join(', ')} = ${finalRoll}`
      );
    } else if (extraDice > 0) {
      // Extra dice without discarding (e.g., Maximal adds dice that all count)
      finalRoll = allDiceRolls.reduce((sum, d) => sum + d, 0);
      contributingDice = allDiceRolls;
    } else {
      // Standard single die roll
      finalRoll = allDiceRolls[0];
      contributingDice = [allDiceRolls[0]];
    }

    // Handle Rerolls (applied after Tearing)
    let rerollThreshold = 0;
    let canReroll = false;

    ruleEffects.forEach((effect) => {
      if (effect.type === 'diceReroll' && effect.target === 'damageRoll') {
        const threshold = Number(effect.value);
        rerollThreshold = Math.max(rerollThreshold, threshold);

        if (allowRerolls) {
          if (effect.userChoice) {
            if (finalRoll <= threshold) canReroll = true;
          } else {
            // Auto-reroll
            const newRoll = Math.floor(this.rng() * dieType) + 1;
            console.log(`[SpecialRule] ${effect.description}: Rerolled ${finalRoll} -> ${newRoll}`);
            finalRoll = newRoll;
          }
        }
      }
    });

    const damageModTotal = damageModifiers.reduce((sum, mod) => sum + mod.value, 0);
    // Note: Degrees of Success are separate from the roll logic here usually passed in?
    // In performAttackRoll, DoS was added to base damage if using the option.
    // For standard calculation, we might assume DoS handling is external or passed via modifiers if needed.
    // BUT, the interface `DamageRoll` has `degreesOfSuccess`.
    // We will just init it to 0 here unless passed in.
    let totalDamage = finalRoll + fixedBonus + strengthBonus + damageModTotal;

    const furyRolls: FuryRoll[] = [];

    // Righteous Fury
    if (dieType === 10 && contributingDice.some((die) => die === dieType)) {
      // Automatic confirmation attempt if standard rules?
      // Actually usually trigger is 10 on d10.
      let furyChain = true;
      let furyAttempts = 0;
      const maxFuryAttempts = 10;

      while (furyChain && furyAttempts < maxFuryAttempts) {
        // Confirm Roll (WS or BS)
        // We need characteristicKey. Usually derived from weapon class.
        // Assuming BS for Ranged, WS for Melee.
        const charKey =
          modifiedWeapon.class === WeaponClass.Melee ? 'weaponSkill' : 'ballisticSkill';

        // This is a recursive roll, should we log it? Maybe as nested related rolls?
        // Let's rely on simple d100 logging or silent check?
        // Usually, Confirmation is a Test.
        const confirmRoll = this.rollCharacteristic(character, charKey, allModifiers, difficulty, {
          forceLog: false
        }); // Don't log main history for confirm

        if (confirmRoll.success) {
          // Bonus damage like Shock? Or just d10?
          // Rules vary: usually d10 damage, potentially infinite.
          // Simplified: d10.
          const bonus = Math.floor(this.rng() * 10) + 1;
          totalDamage += bonus;
          furyRolls.push({
            confirmRoll: confirmRoll.raw,
            confirmSuccess: true,
            bonusDamage: bonus
          });

          if (bonus < 10) furyChain = false;
        } else {
          furyRolls.push({
            confirmRoll: confirmRoll.raw,
            confirmSuccess: false,
            bonusDamage: 0
          });
          furyChain = false;
        }
        furyAttempts++;
      }
    }

    const damageRoll: DamageRoll = {
      weapon: modifiedWeapon.displayName,
      baseDamage: modifiedWeapon.finalDamage,
      strengthBonus,
      degreesOfSuccess: 0, // Assigned later by consumer if needed
      modifiers: damageModifiers,
      total: totalDamage,
      penetration: modifiedWeapon.finalPenetration,
      type: modifiedWeapon.damageType ?? DamageType.Impact,
      dieRoll: finalRoll,
      canReroll,
      rerollThreshold
    };

    // Collect active special rule notes for display
    const ruleNotes: string[] = [];
    if (tearingInfo) ruleNotes.push('Tearing');
    if (diceMinimum > 0) ruleNotes.push(`Proven (${diceMinimum})`);
    ruleEffects.forEach((effect) => {
      if (effect.type === 'armorInteraction' && effect.target === 'armorPoints') {
        if (effect.description?.includes('double')) ruleNotes.push('Primitive: Armour ×2');
        else if (effect.description?.includes('ignor'))
          ruleNotes.push('Power Field: Ignores Armour');
      }
      if (effect.type === 'damageModifier' && effect.description?.includes('Unnatural Toughness')) {
        ruleNotes.push(`Felling (${effect.value || '?'})`);
      }
    });
    if (ruleNotes.length > 0) {
      (damageRoll as DamageRoll & { ruleNotes?: string[] }).ruleNotes = ruleNotes;
    }

    // LOGGING
    // If context is provided, we log.
    // Specially for RF and Tearing, we want to append info.
    if (logContext) {
      if (logContext.parentEntryId) {
        const rfDetails =
          furyRolls.length > 0 ? ` + RF(${furyRolls.map((f) => f.bonusDamage).join('+')})` : '';
        const tearingDetails = tearingInfo
          ? ` [Tearing: ${tearingInfo.allRolls.join(',')} → dropped ${tearingInfo.discardedRoll}]`
          : '';
        const baseDetails = `${finalRoll}${fixedBonus ? '+' + fixedBonus : ''}${strengthBonus ? '+' + strengthBonus : ''}${damageModTotal ? '+' + damageModTotal : ''}`;
        const damageTypeAbbreviation =
          damageRoll.type === DamageType.Explosive ? 'X' : damageRoll.type.charAt(0).toUpperCase();

        const { updateEntry } = useRollHistory.getState();
        updateEntry(logContext.parentEntryId, {
          relatedRolls: [
            {
              title: logContext.title || `Damage (${modifiedWeapon.displayName})`,
              total: totalDamage,
              details: logContext.compactDamageDetails
                ? `${damageTypeAbbreviation} (${baseDetails}) | Pen ${damageRoll.penetration}${tearingDetails}${rfDetails}`
                : `${baseDetails} (Pen: ${damageRoll.penetration})${tearingDetails}${rfDetails}`
            }
          ]
        });
      }
    }

    return { damageRoll, furyRolls, tearingInfo };
  }

  private getProficiencyModifier(
    weapon: Weapon,
    proficiencies: CharacterProficiencies,
    talents?: Talent[]
  ): Modifier | null {
    const weaponName = gameDataService.getItemDisplayName(weapon.sourceId);
    const weaponAsShopItem = {
      id: weapon.id,
      name: weaponName,
      type: 'weapon' as const,
      subtype: weapon.class,
      proficiencyRequired: (weapon as any).proficiencyRequired,
      weaponClass: weapon.class,
      weight: weapon.weight,
      availability: weapon.availability,
      cost: weapon.cost,
      description: weapon.description,
      rulebook: weapon.rulebook,
      sourceId: weapon.sourceId
    };

    const isProficient = ProficiencyService.isProficientWithWeapon(
      weaponAsShopItem,
      proficiencies,
      talents
    );
    if (isProficient) return null;

    // Check if it's an exotic weapon
    const proficiencyRequired = (weapon as any).proficiencyRequired;
    let requirementDescription: string;

    if (proficiencyRequired === 'Exotic') {
      requirementDescription = `Requires specific proficiency with ${weaponName}`;
    } else {
      requirementDescription = `Requires proficiency with ${proficiencyRequired || weapon.class} weapons`;
    }

    return {
      name: 'Non-Proficient Penalty',
      value: -20,
      source: ModifierSource.Proficiency,
      description: requirementDescription
    };
  }

  rollCombatAttack(
    character: CombatActor,
    skillId: string,
    attackType: AttackType,
    additionalModifiers: Modifier[] = [],
    difficulty: Difficulty = Difficulty.Challenging,
    weapon?: Weapon,
    logContext?: LogContext
  ): CombatRoll {
    const modifiers = [...additionalModifiers];
    let proficiencyPenaltyInfo: RollResult['proficiencyPenalty'] | undefined;

    if (weapon) {
      const proficiencies = ProficiencyService.calculateProficiencies(character.talents);
      const proficiencyModifier = this.getProficiencyModifier(
        weapon,
        proficiencies,
        character.talents
      );
      if (proficiencyModifier) {
        modifiers.push(proficiencyModifier);
        proficiencyPenaltyInfo = {
          applied: true,
          amount: -20,
          reason: proficiencyModifier.description || 'Not proficient',
          suggestedTalents: [
            `Weapon Training (${(weapon as any).proficiencyRequired || weapon.class})`
          ]
        };
      }
    }

    // Find the skill to get the characteristic
    const skill = character.skills.find((s) => s.id === skillId);
    const characteristicKey =
      skill?.characteristic || (attackType === AttackType.Melee ? 'weaponSkill' : 'ballisticSkill');
    const characteristicValue =
      character.characteristics[characteristicKey as keyof Characteristics];

    // Build all modifiers
    const allModifiers: Modifier[] = [
      this.createCharacteristicModifier(characteristicKey, characteristicValue),
      ...(skill ? [this.createSkillModifier(skill.name, skill.training)] : []),
      {
        name: 'Difficulty',
        value: difficulty,
        source: ModifierSource.Situational,
        description: `${Difficulty[difficulty]} difficulty`
      },
      ...modifiers
    ];

    const raw = this.rollD100();
    const target = this.applyModifiers(0, allModifiers);
    const degrees = this.calculateDegrees(raw, target);
    const success = raw <= target;

    const combatRoll: CombatRoll = {
      raw,
      target,
      modifiers: allModifiers,
      total: target,
      degrees,
      success,
      tags: ['combat', attackType, RollType.Skill],
      timestamp: new Date(),
      attackType,
      weapon: weapon ? gameDataService.getItemDisplayName(weapon.sourceId) : skill?.name || skillId,
      proficiencyPenalty: proficiencyPenaltyInfo
    };

    // Add to local history
    this.addToHistory(combatRoll);

    const entryId = this.logRoll(combatRoll, logContext);
    if (entryId) combatRoll.entryId = entryId;

    return combatRoll;
  }

  rollInitiative(
    character: Character,
    additionalModifiers: Modifier[] = [],
    logContext?: LogContext
  ): RollResult {
    const agilityBonus = Math.floor(character.characteristics.agility / 10);
    const modifiers: Modifier[] = [
      {
        name: 'Agility Bonus',
        value: agilityBonus,
        source: ModifierSource.Characteristic,
        description: ''
      },
      ...additionalModifiers
    ];

    // Weapon Master +2 initiative (always applies if trait present — weapon is assumed equipped)
    const initMods = CareerAbilityService.getInitiativeModifiersFromTraits(character);
    modifiers.push(...initMods);

    const raw = this.rollD100();
    const target = agilityBonus + additionalModifiers.reduce((sum, m) => sum + m.value, 0);

    const result: RollResult = {
      raw,
      target,
      modifiers,
      total: raw + target,
      degrees: 0, // Initiative doesn't use degrees
      success: true, // Initiative is always "successful"
      tags: [RollType.Initiative],
      timestamp: new Date()
    };

    // Add to local history
    this.addToHistory(result);

    const entryId = this.logRoll(result, logContext);
    if (entryId) result.entryId = entryId;

    return result;
  }

  // Helpers
  calculateSemiAutoHits(degrees: number, rof: number): number {
    return Math.min(1 + Math.floor(degrees / 2), rof);
  }
  calculateScatterHits(degrees: number, pointBlank: boolean): number {
    return pointBlank ? Math.floor(degrees / 2) : 0;
  }
  calculateFullAutoHits(degrees: number, rof: number): number {
    return Math.min(1 + degrees, rof);
  }
}

export const diceService = new DiceService();
